import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { InvoiceProvider, useInvoice } from './context/InvoiceContext';
import { ConfirmProvider } from './context/ConfirmContext';
import Header from './components/Header';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Users from './pages/Users';
import Timesheet from './pages/Timesheet';
import Todos from './pages/Todos';
import Vacation from './pages/Vacation';
import Policies from './pages/Policies';
import GenerateInvoice from './pages/GenerateInvoice';
import Quotations from './pages/Quotations';
import Receipts from './pages/Receipts';
import Customers from './pages/Customers';
import Leads from './pages/Leads';
import Interviews from './pages/Interviews';
import Products from './pages/Products';
import Sidebar from './components/Sidebar';
import Notification from './components/Notification';

// Persistent layout for all authenticated routes.
// Header mounts ONCE and stays alive across page navigations.
const ProtectedLayout = () => {
  const { currentUser, loading } = useInvoice();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(c => {
      localStorage.setItem('sidebarCollapsed', String(!c));
      return !c;
    });
  };

  if (loading) {
    return (
      <div className="flex-center" style={{ minHeight: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!currentUser) return <Navigate to="/" replace />;

  const isMobile = window.innerWidth < 768;
  if (isMobile && currentUser.role !== 'super_admin') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', textAlign: 'center', background: 'var(--color-gray-50)' }}>
        <div>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>💻</div>
          <h2 style={{ marginBottom: '8px', color: 'var(--color-text)' }}>Desktop Only</h2>
          <p style={{ color: 'var(--color-secondary)', fontSize: '14px' }}>Please use a laptop or desktop to access this portal.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header onToggleSidebar={() => setSidebarOpen(o => !o)} sidebarCollapsed={sidebarCollapsed} onToggleCollapse={toggleSidebarCollapsed} />
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Sidebar mobileOpen={sidebarOpen} onMobileClose={() => setSidebarOpen(false)} collapsed={sidebarCollapsed} />
        <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

// Employee (User role) restriction — locked to Todos/Timesheets/Calendar/Leave Policy/Profile,
// plus anything a specific permission grant unlocks. Note: create_invoice defaults to
// true for every employee (it governs whether they can create invoices when they DO
// have Invoices access elsewhere in the app), so it can't be used as an escape hatch
// here — Invoices stays a hard lock for restricted employees, no exceptions.
const EMPLOYEE_ALLOWED_PATHS = ['/work/todos', '/work/timesheets', '/work/leave', '/work/policies', '/profile'];
const EmployeeRestricted = () => {
  const { currentUser, hasPermission } = useInvoice();
  const location = useLocation();
  if (currentUser?.role === 'employee') {
    const allowed = EMPLOYEE_ALLOWED_PATHS.some(p => location.pathname.startsWith(p))
      || (location.pathname.startsWith('/hr/interviews') && hasPermission('manage_interviews'))
      || (location.pathname.startsWith('/leads') && hasPermission('manage_leads'));
    if (!allowed) return <Navigate to="/work/todos" replace />;
  }
  return <Outlet />;
};

// Admin-only guard — nested inside ProtectedLayout
const AdminOnly = () => {
  const { currentUser } = useInvoice();
  if (!['admin', 'super_admin'].includes(currentUser?.role)) {
    return <Navigate to="/work/todos" replace />;
  }
  return <Outlet />;
};

// Interviews guard — admins, or anyone granted the manage_interviews capability
const InterviewsOnly = () => {
  const { currentUser, hasPermission } = useInvoice();
  if (!['admin', 'super_admin'].includes(currentUser?.role) && !hasPermission('manage_interviews')) {
    return <Navigate to="/work/todos" replace />;
  }
  return <Outlet />;
};

// Leads guard — admins, or anyone granted the manage_leads capability
const LeadsOnly = () => {
  const { currentUser, hasPermission } = useInvoice();
  if (!['admin', 'super_admin'].includes(currentUser?.role) && !hasPermission('manage_leads')) {
    return <Navigate to="/work/todos" replace />;
  }
  return <Outlet />;
};

// Public Route — redirects logged-in users away
const PublicRoute = ({ children }) => {
  const { currentUser, loading } = useInvoice();

  if (loading) {
    return (
      <div className="flex-center" style={{ minHeight: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (currentUser) return <Navigate to="/work/todos" replace />;
  return children;
};

function AppRoutes() {
  return (
    <>
      <Notification />
      <Routes>
        <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

        {/* All authenticated routes share one persistent Header via ProtectedLayout */}
        <Route element={<ProtectedLayout />}>
          {/* User (employee) role is locked to Todos/Timesheets/Calendar/Leave Policy/Profile,
              plus anything specific permission grants unlock */}
          <Route element={<EmployeeRestricted />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route element={<LeadsOnly />}>
              <Route path="/leads" element={<Leads />} />
            </Route>
            <Route path="/generate-invoice" element={<GenerateInvoice />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/products" element={<Products />} />
            <Route element={<AdminOnly />}>
              <Route path="/quotations" element={<Quotations />} />
              <Route path="/receipts" element={<Receipts />} />
            </Route>
            <Route path="/profile" element={<Profile />} />

            {/* Work routes */}
            <Route path="/work" element={<Navigate to="/work/todos" replace />} />
            <Route path="/work/timesheets" element={<Timesheet />} />
            <Route path="/work/todos" element={<Todos />} />
            <Route path="/work/leave" element={<Vacation />} />
            {/* Trainings and Reminders hidden for now */}
            <Route path="/work/trainings" element={<Navigate to="/work/todos" replace />} />
            <Route path="/work/reminders" element={<Navigate to="/work/todos" replace />} />
            <Route path="/work/policies" element={<Policies />} />

            {/* People — admin/super_admin only */}
            <Route element={<AdminOnly />}>
              <Route path="/people" element={<Users />} />
            </Route>

            {/* Interviews — admins, or users granted the manage_interviews capability */}
            <Route element={<InterviewsOnly />}>
              <Route path="/hr/interviews" element={<Interviews />} />
            </Route>

            {/* Legacy redirects */}
            <Route path="/timesheet" element={<Navigate to="/work/timesheets" replace />} />
            <Route path="/todos" element={<Navigate to="/work/todos" replace />} />
            <Route path="/users" element={<Navigate to="/people" replace />} />
            <Route path="/employees" element={<Navigate to="/work/timesheets" replace />} />
            <Route path="/employees/timesheets" element={<Navigate to="/work/timesheets" replace />} />
            <Route path="/employees/todos" element={<Navigate to="/work/todos" replace />} />
            <Route path="/employees/people" element={<Navigate to="/people" replace />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <InvoiceProvider>
        <ConfirmProvider>
          <AppRoutes />
        </ConfirmProvider>
      </InvoiceProvider>
    </Router>
  );
}

export default App;
